//
// FIXME: This is not a React component, so the file should end in .ts, not .tsx.
//

import { defineInvisibleReCaptcha2Button, getCaptchaActionEnabled } from '@rcm/recaptcha-2';
import { SIGNUP_CUSTOMER } from '@reliv/cart';
import angular from 'angular';
import type { CartGeoCodeManagerState, CartOrder, CartOrderManager, CartProfile } from 'cart-core';
import { getContentArea } from 'cart-core';
import { getSessionSponsorRcn } from 'sponsor-rcn-affiliate-links';
import type GeoCodeManager from '../../service/geo-code-4';
import SignUpAngularController from '../SignUpAngularController';

interface Response {
  data: CartProfile;
}

const messageNamespace = 'checkoutCustomer';

class CheckoutCustomerController extends SignUpAngularController {
  public showForm: boolean;
  public showConfirmation: boolean;
  public myPage: string;
  public hasMessage: boolean;
  public fieldMessages: { [key: string]: string };
  public mailingAddressMeta: {
    hasStates: boolean;
    states: string[];
  };
  public geoCodeState: Pick<CartGeoCodeManagerState, 'geoCodes' | 'haveGeoCodeOptions' | 'selectedGeoCode'>;
  public geoCode: GeoCodeManager;
  public sponsorRcnAgain: string;
  public passwordAgain: string;
  public disclaimerMessage = this.$sce.trustAsHtml(getContentArea('checkoutCustomerDisclaimer') || '');

  constructor(
    public $sce: angular.ISCEService,
    public $location: any,
    public rcmEventManager: IRcmApiLibEventManager,
    public orderMgr: CartOrderManager,
    public checkoutEnrollment: any,
    public allowedStates: any,
    public geoCode4Factory: any
  ) {
    super();

    defineInvisibleReCaptcha2Button();

    this.showForm = true;
    this.showConfirmation = false;
    this.myPage = '/checkout/customer';
    this.hasMessage = false;
    this.fieldMessages = {};
    this.newProfile = {
      signupReferrer: '', // Server validation requires this key to exist
    } as CartProfile;
    this.haveSponsor = true;
    this.mailingAddressMeta = {
      hasStates: false,
      states: [],
    };
    this.newProfile.mailingAddressCountry = '';

    this.geoCodeState = {
      geoCodes: [],
      haveGeoCodeOptions: false,
      selectedGeoCode: null,
    };

    this.geoCode = geoCode4Factory.createInstance(this.handleGeoCodeStateChanged);

    /**
     * on orderMgr.orderChange
     */
    rcmEventManager.on('orderMgr.orderChange', this.updateOrder, 'checkoutCustomer');
  }

  public shouldShowSponsorRcn() {
    return !this.order?.attributes?.signUpRules?.customer?.requiredSponsorRcn && this.haveSponsor !== false;
  }

  public shouldShowProfilePrimaryFields() {
    return !this.showConfirmation;
  }

  public handleGeoCodeStateChanged = (geoCodeState) => {
    this.geoCodeState = geoCodeState;
    if (this.geoCodeState.selectedGeoCode && this.geoCodeState.selectedGeoCode.geoCode) {
      this.newProfile.mailingAddressGeoCode = this.geoCodeState.selectedGeoCode.geoCode;
      this.newProfile.mailingAddressState = this.geoCodeState.selectedGeoCode.state;
      this.newProfile.mailingAddressCity = this.geoCodeState.selectedGeoCode.city;
    }
  };

  public geoCodeInit = () => {
    if (this.order.attributes.countryRules.collectGeo && this.isMyPage()) {
      this.geoCode.handleChange(this.newProfile.mailingAddressPostalCode, {
        city: this.newProfile.mailingAddressCity,
        geoCode: this.newProfile.mailingAddressGeoCode,
        state: this.newProfile.mailingAddressState,
      });
    }
  };

  public geoCodeUpdate = () => {
    if (this.order.attributes.countryRules.collectGeo && this.isMyPage()) {
      this.newProfile.mailingAddressGeoCode = null;
      this.newProfile.mailingAddressCity = null;
      this.newProfile.mailingAddressState = null;
      this.geoCode.handleChange(this.newProfile.mailingAddressPostalCode, this.geoCodeState.selectedGeoCode);
    }
  };

  public onSelectShipGeoCode = () => {
    this.handleGeoCodeStateChanged(this.geoCodeState);
  };

  public onPostalCodeChange = () => {
    this.geoCodeUpdate();
  };

  public handleHaveSponsorChanged = (value) => {
    if (value) {
      this.newProfile.sponsorRcn = '';
      this.sponsorRcnAgain = '';
    } else {
      this.newProfile.sponsorRcn = this.order.attributes.guestSignUpRules.sponsorRcn;
      this.sponsorRcnAgain = this.order.attributes.guestSignUpRules.sponsorRcn;
    }
  };

  public refreshAvailableStates = () => {
    const country = this.newProfile.mailingAddressCountry;
    const model = this.mailingAddressMeta;
    const countryHasStates =
      this.order.attributes.countryRules.countriesWithNoState.indexOf(country) < 0 &&
      typeof country === 'string' &&
      country !== '';

    if (!countryHasStates) {
      model.hasStates = false;
      model.states = [];

      return;
    }

    this.allowedStates.getStates(country, function (states) {
      model.hasStates = states && states.length > 0;
      model.states = states;
    });
  };

  public handleCountryChanged = () => {
    this.refreshAvailableStates();
  };

  public submit = () => {
    this.onSubmit();

    if (!this.verifySponsorRcn()) {
      return;
    }

    if (!this.verifyPassword()) {
      return;
    }

    const profile = {
      ...this.newProfile,
      // If Google ReCAPTCHA is available, send the current captcha response
      ...(this.getIsCaptchaEnabled() ? { captcha: window.grecaptcha.getResponse() } : {}),
    };

    this.checkoutEnrollment.createProfile(profile, this.onSubmitSuccess, this.onSubmitError);
  };

  public onSubmit = () => {
    this.hasMessage = false;
    this.checkoutEnrollment.clearMessages(messageNamespace);
  };

  public onSubmitSuccess = (response: Response) => {
    if (window.location.hash.includes('checking-out=true')) {
      /* If they got here from not being logged in while checkout out,
       * take them to next checkout step
       */
      window.location.href = '/secure/checkout/v1#/checkout/address';
      return;
    }
    this.newProfile = response.data;
    this.showConfirmation = true;
  };

  public onSubmitError = (response) => {
    // If Google ReCAPTCHA is available, make sure to reset it's internal state following an error
    if (this.getIsCaptchaEnabled()) {
      window.grecaptcha.reset();
    }

    this.checkoutEnrollment.handleMessages(
      response.messages,
      this.setFieldMessages,
      this.onPrimaryMessage,
      messageNamespace
    );
  };

  public setFieldMessages = (messages) => {
    this.fieldMessages = messages;
  };

  public onPrimaryMessage = (messages) => {
    this.hasMessage = true;
  };

  public isMyPage = () => {
    return this.$location.path() === this.myPage;
  };

  public buildNewCustomer = () => {
    // get sponsorRcn from affiliate link system if it is set
    this.newProfile.sponsorRcn = getSessionSponsorRcn();

    /**
     * Populate the sponsor RCN with the required RCN if an RCN is required
     */
    if (this.order.attributes.signUpRules.customer.requiredSponsorRcn) {
      this.newProfile.sponsorRcn = this.order.attributes.signUpRules.customer.requiredSponsorRcn;
    }

    // Sync again field always
    this.sponsorRcnAgain = this.newProfile.sponsorRcn;

    this.newProfile.creationMethod = 'Customer';
    this.newProfile.status = 'C';
    this.newProfile.rank = this.order.attributes.rankRules.defaultRankCustomer;
    this.newProfile.mailingAddressCountry = this.order.attributes.countryRules.countryIso3Code;

    this.showForm = true;
  };

  public init = () => {
    this.buildNewCustomer();
    this.refreshAvailableStates();
    this.geoCodeInit();
  };

  public updateOrder = (order: CartOrder) => {
    this.order = order;
    if (this.isMyPage()) {
      this.init();
    }
  };

  public getIsCaptchaEnabled() {
    return getCaptchaActionEnabled(SIGNUP_CUSTOMER) && !!window?.grecaptcha?.getResponse;
  }
}

angular
  .module('checkout')
  .controller('checkoutCustomer', [
    '$sce',
    '$location',
    'rcmEventManager',
    'orderMgr',
    'checkoutEnrollment',
    'allowedStates',
    'geoCode4Factory',
    CheckoutCustomerController as angular.IControllerConstructor,
  ]);
